// Column registry and CSV builder for the sales export.
// Used by QuickViewStats and SystemSettings via the ExportConfigModal flow.

const formatDateTime = (value) => {
  if (!value) return ['', ''];
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return ['', ''];
  return [d.toLocaleDateString(), d.toLocaleTimeString()];
};

const dateOf = (value) => formatDateTime(value)[0];
const timeOf = (value) => formatDateTime(value)[1];

// Each column: { id, labelKey, getValue }.
// getValue receives one row from /api/admin/export-statistics and returns
// the raw cell value (string|number). CSV escaping happens centrally in buildCsv.
export const EXPORT_COLUMNS = [
  { id: 'orderId',           labelKey: 'exportColOrderId',           getValue: (r) => r.order_id ?? '' },
  { id: 'orderName',         labelKey: 'exportColOrderName',         getValue: (r) => r.order_name ?? '' },
  { id: 'orderStatus',       labelKey: 'exportColOrderStatus',       getValue: (r) => r.order_status ?? '' },
  { id: 'orderCreatedDate',  labelKey: 'exportColOrderCreatedDate',  getValue: (r) => dateOf(r.order_created) },
  { id: 'orderCreatedTime',  labelKey: 'exportColOrderCreatedTime',  getValue: (r) => timeOf(r.order_created) },
  { id: 'orderModifiedDate', labelKey: 'exportColOrderModifiedDate', getValue: (r) => dateOf(r.order_closed_at || r.order_modified) },
  { id: 'orderModifiedTime', labelKey: 'exportColOrderModifiedTime', getValue: (r) => timeOf(r.order_closed_at || r.order_modified) },
  { id: 'orderTip',          labelKey: 'exportColOrderTip',          getValue: (r) => r.order_tip ?? '0' },
  { id: 'paymentType',       labelKey: 'exportColPaymentType',       getValue: (r) => r.payment_type_name ?? '' },
  { id: 'tableName',         labelKey: 'exportColTableName',         getValue: (r) => r.table_name ?? '' },
  { id: 'openedBy',          labelKey: 'exportColOpenedBy',          getValue: (r) => r.opened_by ?? '' },
  { id: 'closedBy',          labelKey: 'exportColClosedBy',          getValue: (r) => r.closed_by ?? '' },
  { id: 'orderProductId',    labelKey: 'exportColOrderProductId',    getValue: (r) => r.order_product_id ?? '' },
  { id: 'quantity',          labelKey: 'exportColQuantity',          getValue: (r) => r.quantity ?? '0' },
  { id: 'itemPrice',         labelKey: 'exportColItemPrice',         getValue: (r) => r.item_price ?? '0' },
  { id: 'itemDiscount',      labelKey: 'exportColItemDiscount',      getValue: (r) => r.item_discount ?? '0' },
  { id: 'itemNote',          labelKey: 'exportColItemNote',          getValue: (r) => r.item_note ?? '' },
  { id: 'itemStatus',        labelKey: 'exportColItemStatus',        getValue: (r) => r.item_status ?? '0' },
  { id: 'dontMake',          labelKey: 'exportColDontMake',          getValue: (r) => r.dont_make ?? '0' },
  { id: 'itemCreatedDate',   labelKey: 'exportColItemCreatedDate',   getValue: (r) => dateOf(r.item_created) },
  { id: 'itemCreatedTime',   labelKey: 'exportColItemCreatedTime',   getValue: (r) => timeOf(r.item_created) },
  { id: 'itemModifiedDate',  labelKey: 'exportColItemModifiedDate',  getValue: (r) => dateOf(r.item_modified) },
  { id: 'itemModifiedTime',  labelKey: 'exportColItemModifiedTime',  getValue: (r) => timeOf(r.item_modified) },
  { id: 'productName',       labelKey: 'exportColProductName',       getValue: (r) => r.product_name ?? '' },
  { id: 'productDescription',labelKey: 'exportColProductDescription',getValue: (r) => r.product_description ?? '' },
  { id: 'categoryName',      labelKey: 'exportColCategoryName',      getValue: (r) => r.category_name ?? '' },
  { id: 'categoryArea',      labelKey: 'exportColCategoryArea',      getValue: (r) => r.category_area ?? '' },
  { id: 'lineTotal',         labelKey: 'exportColLineTotal',         getValue: (r) => r.line_total ?? '0' },
  { id: 'orderedDate',       labelKey: 'exportColOrderedDate',       getValue: (r) => dateOf(r.ordered_timestamp) },
  { id: 'orderedTime',       labelKey: 'exportColOrderedTime',       getValue: (r) => timeOf(r.ordered_timestamp) },
  { id: 'inProgressDate',    labelKey: 'exportColInProgressDate',    getValue: (r) => dateOf(r.inprogress_timestamp) },
  { id: 'inProgressTime',    labelKey: 'exportColInProgressTime',    getValue: (r) => timeOf(r.inprogress_timestamp) },
  { id: 'finishedDate',      labelKey: 'exportColFinishedDate',      getValue: (r) => dateOf(r.finished_timestamp) },
  { id: 'finishedTime',      labelKey: 'exportColFinishedTime',      getValue: (r) => timeOf(r.finished_timestamp) },
  { id: 'totalTimeMinutes',  labelKey: 'exportColTotalTimeMinutes',  getValue: (r) => r.total_time_minutes ?? '' },
  { id: 'waitTimeMinutes',   labelKey: 'exportColWaitTimeMinutes',   getValue: (r) => r.wait_time_minutes ?? '' },
  { id: 'prepTimeMinutes',   labelKey: 'exportColPrepTimeMinutes',   getValue: (r) => r.prep_time_minutes ?? '' },
];

const COLUMNS_BY_ID = EXPORT_COLUMNS.reduce((acc, c) => { acc[c.id] = c; return acc; }, {});

export const getColumnById = (id) => COLUMNS_BY_ID[id];

// Full set — matches today's QuickViewStats export (37 columns, in current order).
export const DEFAULT_COLUMNS_FULL = EXPORT_COLUMNS.map((c) => c.id);

// Narrower set — matches today's SystemSettings export (29 columns, in current order).
export const DEFAULT_COLUMNS_BASIC = [
  'orderId', 'orderName', 'orderStatus',
  'orderCreatedDate', 'orderCreatedTime', 'orderModifiedDate', 'orderModifiedTime',
  'orderTip', 'paymentType', 'tableName', 'openedBy', 'closedBy',
  'orderProductId', 'quantity', 'itemPrice', 'itemDiscount',
  'itemNote', 'itemStatus',
  'productName', 'categoryName', 'categoryArea', 'lineTotal',
  'orderedDate', 'orderedTime', 'finishedDate', 'finishedTime',
  'totalTimeMinutes', 'waitTimeMinutes', 'prepTimeMinutes',
];

// CSV-escape a cell value. Numeric/empty stay bare; strings get quoted with "" escape.
const escapeCell = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return String(value);
  const s = String(value);
  // Quote any string that could confuse a CSV reader, plus all text-ish content.
  // We always quote non-numeric strings to match the prior implementation's behavior
  // (which quoted every text field unconditionally).
  if (/^-?\d+(\.\d+)?$/.test(s)) return s;
  return `"${s.replace(/"/g, '""')}"`;
};

/**
 * Build a CSV string from rows using the user-selected columns and language.
 *
 * @param {Array<object>} rows           - rows from /api/admin/export-statistics
 * @param {Array<string>} selectedIds    - column ids in user-chosen order
 * @param {(key: string, lang: string) => string} translateInLanguage
 *                                        - translation fn that respects an explicit lang
 * @param {string} language              - 'en' | 'es'
 * @returns {string} CSV content
 */
export function buildCsv(rows, selectedIds, translateInLanguage, language) {
  const cols = selectedIds
    .map((id) => COLUMNS_BY_ID[id])
    .filter(Boolean);

  const headerRow = cols
    .map((c) => escapeCell(translateInLanguage(c.labelKey, language)))
    .join(',');

  const dataRows = rows.map((row) =>
    cols.map((c) => escapeCell(c.getValue(row))).join(',')
  );

  return [headerRow, ...dataRows].join('\n');
}

// localStorage helpers — separate slot per caller so each remembers its own preset.
const storageKeyFor = (callerKey) => `pos-export-config:${callerKey}`;

export function loadExportConfig(callerKey, fallback) {
  try {
    const raw = localStorage.getItem(storageKeyFor(callerKey));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return fallback;
    const validIds = (parsed.selectedColumns || []).filter((id) => COLUMNS_BY_ID[id]);
    if (validIds.length === 0) return fallback;
    const language = parsed.language === 'en' || parsed.language === 'es' ? parsed.language : fallback.language;
    return { language, selectedColumns: validIds };
  } catch {
    return fallback;
  }
}

export function saveExportConfig(callerKey, config) {
  try {
    localStorage.setItem(storageKeyFor(callerKey), JSON.stringify(config));
  } catch {
    // localStorage may be unavailable (private mode, quota); silently skip.
  }
}
